import { s } from '../../theme/tokens';
import { chartSwatchContainerWidthPx, chartSwatchRadiusPx, chartSwatchSizePx } from './ChartSwatch';
import {
  buildTooltipOverlaySurfaceSx,
  tooltipOverlayBodyFontWeight,
  tooltipOverlayCompactLineHeight,
  tooltipOverlayFontSize,
  tooltipOverlayLabelFontWeight,
  tooltipOverlayLineHeight,
  tooltipOverlayMetaFontSize,
  tooltipOverlayMetricFontWeight,
  tooltipOverlayPaddingX,
  tooltipOverlayPaddingY,
  tooltipOverlayTextColor,
  tooltipOverlayTitleFontSize,
} from './tooltipOverlayStyles';
import type { SxProps, Theme } from '@mui/material/styles';

type BuildStackedBarChartTooltipSxParams = {
  borderColor: string;
  textColor: string;
  tooltipFontSize: number | string;
  tooltipFontWeight: number | string;
  tooltipLineHeight: number | string;
};

export const buildStackedBarChartGlobalTooltipStyles = (
  theme: Theme,
): Record<string, Record<string, number | string>> => ({
  '.flowqa-stacked-tooltip-paper': {
    ...buildTooltipOverlaySurfaceSx(theme),
    maxWidth: 'min(calc(100vw - 32px), 720px)',
    width: 'max-content',
    paddingTop: tooltipOverlayPaddingY,
    paddingBottom: tooltipOverlayPaddingY,
    paddingLeft: '0px',
    paddingRight: '0px',
    fontSize: tooltipOverlayFontSize,
    lineHeight: tooltipOverlayLineHeight,
    fontWeight: tooltipOverlayBodyFontWeight,
  },
  '.flowqa-stacked-tooltip-paper *': {
    fontSize: tooltipOverlayFontSize,
    lineHeight: tooltipOverlayLineHeight,
    fontWeight: tooltipOverlayBodyFontWeight,
  },
  '.flowqa-stacked-tooltip-table': {
    borderSpacing: 0,
    border: '0 !important',
    width: '100% !important',
    tableLayout: 'auto',
  },
  '.flowqa-stacked-tooltip-table > thead > tr > th, .flowqa-stacked-tooltip-table > thead > tr > td':
    {
      borderBottom: '0 !important',
      paddingTop: '0px',
      paddingBottom: tooltipOverlayPaddingY,
      paddingLeft: `${tooltipOverlayPaddingX} !important`,
      paddingRight: `${tooltipOverlayPaddingX} !important`,
      color: `${tooltipOverlayTextColor} !important`,
      fontSize: `${tooltipOverlayTitleFontSize} !important`,
      lineHeight: tooltipOverlayCompactLineHeight,
      textAlign: 'left',
      verticalAlign: 'middle !important',
    },
  '.flowqa-stacked-tooltip-table > thead > tr > th': {
    fontWeight: tooltipOverlayLabelFontWeight,
    paddingRight: `${s('2xl')} !important`,
  },
  '.flowqa-stacked-tooltip-table > thead > tr > td.flowqa-stacked-tooltip-header-total-label': {
    textAlign: 'right !important',
    whiteSpace: 'nowrap',
  },
  '.flowqa-stacked-tooltip-table > thead > tr > td.flowqa-stacked-tooltip-header-total-value': {
    textAlign: 'right !important',
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
  },
  '.flowqa-stacked-tooltip-divider-row': {
    height: '1px !important',
  },
  '.flowqa-stacked-tooltip-divider-cell': {
    backgroundColor: 'transparent !important',
    backgroundImage: `linear-gradient(${theme.app.colors.border}, ${theme.app.colors.border}) !important`,
    backgroundPosition: 'center top !important',
    backgroundRepeat: 'no-repeat !important',
    backgroundSize: `calc(100% - (${tooltipOverlayPaddingX} * 2)) 1px !important`,
    borderBottom: '0 !important',
    borderTop: '0 !important',
    fontSize: '0 !important',
    height: '0 !important',
    lineHeight: '0 !important',
    padding: '0 !important',
  },
  '.flowqa-stacked-tooltip-table > tbody > tr > th, .flowqa-stacked-tooltip-table > tbody > tr > td':
    {
      border: '0 !important',
      paddingLeft: `${tooltipOverlayPaddingX} !important`,
      paddingRight: `${tooltipOverlayPaddingX} !important`,
      verticalAlign: 'middle !important',
    },
  '.flowqa-stacked-tooltip-table > tbody > tr.flowqa-stacked-tooltip-total-row > th, .flowqa-stacked-tooltip-table > tbody > tr.flowqa-stacked-tooltip-total-row > td':
    {
      paddingTop: '8px !important',
      textAlign: 'left !important',
    },
  '.flowqa-stacked-tooltip-table > tbody > tr:first-of-type > th, .flowqa-stacked-tooltip-table > tbody > tr:first-of-type > td':
    {
      paddingTop: `${tooltipOverlayPaddingY} !important`,
    },
  '.flowqa-stacked-tooltip-table > tbody > tr:last-of-type > th, .flowqa-stacked-tooltip-table > tbody > tr:last-of-type > td':
    {
      paddingBottom: '6px !important',
    },
  '.flowqa-stacked-tooltip-table .MuiChartsTooltip-markContainer': {
    width: `${chartSwatchContainerWidthPx}px`,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    lineHeight: 0,
    transform: 'translateY(-1px)',
    verticalAlign: 'middle',
  },
  '.flowqa-stacked-tooltip-cell': {
    paddingTop: '3px',
    paddingBottom: '3px',
    lineHeight: tooltipOverlayCompactLineHeight,
  },
  '.flowqa-stacked-tooltip-count-cell': {
    textAlign: 'right !important',
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
    fontWeight: tooltipOverlayMetricFontWeight,
  },
  '.flowqa-stacked-tooltip-percent-cell': {
    color: `${tooltipOverlayTextColor} !important`,
    fontSize: `${tooltipOverlayMetaFontSize} !important`,
    paddingLeft: '10px !important',
    textAlign: 'right !important',
    whiteSpace: 'nowrap',
    minWidth: '40px',
    fontVariantNumeric: 'tabular-nums',
  },
  '.flowqa-stacked-tooltip-mark': {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: `${chartSwatchSizePx}px !important`,
    height: `${chartSwatchSizePx}px !important`,
    borderRadius: `${chartSwatchRadiusPx}px !important`,
    marginTop: 0,
    marginBottom: 0,
  },
  '.flowqa-stacked-tooltip-paper.flowqa-stacked-tooltip-paper-empty': {
    paddingBottom: tooltipOverlayPaddingY,
  },
});

export const buildStackedBarChartTooltipSx = ({
  borderColor,
  tooltipFontSize,
  tooltipFontWeight,
  tooltipLineHeight,
}: BuildStackedBarChartTooltipSxParams): SxProps<Theme> => ({
  fontSize: tooltipFontSize,
  lineHeight: tooltipLineHeight,
  fontWeight: tooltipFontWeight,
  paddingTop: '0 !important',
  paddingBottom: '0 !important',
  paddingLeft: '0 !important',
  paddingRight: '0 !important',
  '& .MuiChartsTooltip-table > thead > tr > th, & .MuiChartsTooltip-table > thead > tr > td, & table > thead > tr > th, & table > thead > tr > td':
    {
      borderBottom: '0 !important',
      boxShadow: 'none !important',
      paddingLeft: `${tooltipOverlayPaddingX} !important`,
      paddingRight: `${tooltipOverlayPaddingX} !important`,
      paddingTop: '0 !important',
      paddingBottom: `${tooltipOverlayPaddingY} !important`,
      textAlign: 'left',
      verticalAlign: 'middle !important',
    },
  '& .MuiChartsTooltip-table, & table': {
    borderCollapse: 'separate',
    borderSpacing: 0,
    width: '100% !important',
    tableLayout: 'auto',
  },
  '& .flowqa-stacked-tooltip-divider-row': {
    height: '1px !important',
  },
  '& .flowqa-stacked-tooltip-divider-cell': {
    backgroundColor: 'transparent !important',
    backgroundImage: `linear-gradient(${borderColor}, ${borderColor}) !important`,
    backgroundPosition: 'center top !important',
    backgroundRepeat: 'no-repeat !important',
    backgroundSize: `calc(100% - (${tooltipOverlayPaddingX} * 2)) 1px !important`,
    borderBottom: '0 !important',
    borderTop: '0 !important',
    fontSize: '0 !important',
    height: '0 !important',
    lineHeight: '0 !important',
    padding: '0 !important',
  },
  '& .MuiChartsTooltip-markContainer': {
    width: `${chartSwatchContainerWidthPx}px !important`,
    display: 'inline-flex !important',
    alignItems: 'center !important',
    justifyContent: 'flex-start !important',
    lineHeight: '0 !important',
    transform: 'translateY(-1px)',
    verticalAlign: 'middle !important',
  },
  '& .MuiChartsTooltip-row, & tr': {
    lineHeight: `${String(tooltipOverlayCompactLineHeight)} !important`,
  },
  '& .MuiTypography-root, & .MuiChartsTooltip-cell, & .MuiChartsTooltip-labelCell, & .MuiChartsTooltip-valueCell, & .MuiChartsTooltip-axisValueCell, & caption, & td, & th':
    {
      color: tooltipOverlayTextColor,
      fontSize: tooltipFontSize,
      lineHeight: tooltipLineHeight,
      fontWeight: tooltipFontWeight,
      textAlign: 'left !important',
    },
  '& .MuiChartsTooltip-cell, & .MuiChartsTooltip-markCell, & .MuiChartsTooltip-labelCell, & .MuiChartsTooltip-valueCell':
    {
      paddingTop: '3px !important',
      paddingBottom: '3px !important',
      lineHeight: `${String(tooltipOverlayCompactLineHeight)} !important`,
      verticalAlign: 'middle !important',
    },
  '& .MuiChartsTooltip-table > tbody > tr.flowqa-stacked-tooltip-total-row > th, & .MuiChartsTooltip-table > tbody > tr.flowqa-stacked-tooltip-total-row > td':
    {
      paddingTop: '8px !important',
      textAlign: 'left !important',
    },
  '& .MuiChartsTooltip-table > tbody > tr:first-of-type > th, & .MuiChartsTooltip-table > tbody > tr:first-of-type > td':
    {
      paddingTop: `${tooltipOverlayPaddingY} !important`,
    },
  '& .MuiChartsTooltip-table > tbody > tr:last-of-type > th, & .MuiChartsTooltip-table > tbody > tr:last-of-type > td':
    {
      paddingBottom: '2px !important',
    },
  '& .flowqa-stacked-tooltip-count-cell, & .flowqa-stacked-tooltip-percent-cell': {
    textAlign: 'right !important',
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
    fontWeight: tooltipOverlayMetricFontWeight,
  },
  '& .flowqa-stacked-tooltip-percent-cell': {
    color: tooltipOverlayTextColor,
    fontSize: tooltipOverlayMetaFontSize,
    fontWeight: tooltipOverlayBodyFontWeight,
    minWidth: '40px',
    paddingLeft: '10px !important',
  },
  '& .MuiChartsTooltip-mark': {
    display: 'inline-flex !important',
    alignItems: 'center !important',
    justifyContent: 'center !important',
    width: `${chartSwatchSizePx}px !important`,
    height: `${chartSwatchSizePx}px !important`,
    borderRadius: `${chartSwatchRadiusPx}px !important`,
    marginTop: '0 !important',
    marginBottom: '0 !important',
  },
  '& .flowqa-stacked-tooltip-header-total-label': {
    textAlign: 'right !important',
    whiteSpace: 'nowrap',
  },
  '& .flowqa-stacked-tooltip-header-total-value': {
    textAlign: 'right !important',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  },
  '& .MuiChartsTooltip-axisValue, & .MuiChartsTooltip-axisValueCell': {
    marginTop: '0 !important',
    marginBottom: '0 !important',
    paddingTop: '0 !important',
    paddingBottom: '0 !important',
    lineHeight: `${String(tooltipOverlayCompactLineHeight)} !important`,
  },
});
